import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  ContentKind,
  DOCUMENT_EXCLUSIVE_MESSAGE,
  DOCUMENT_MIME,
  DOCUMENT_TITLE_MAX_LENGTH,
  LINKEDIN_DOCUMENT_MAX_BYTES,
  LINKEDIN_DOCUMENT_MAX_PAGES,
  POSTABLE_MIMES,
  POSTABLE_MIMES_LABEL,
  SocialPlatform,
  countPostChars,
  documentTitleFromFileName,
  isDocumentMime,
  isPostableMime,
  MEDIA_MIXING_MESSAGE,
  socialPlatformLabel,
  supportsThreads,
} from '@mg/shared';
import type { ApplyMediaToSetResult, ThreadConversionChange } from '@mg/shared';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  FileText,
  Film,
  ImageIcon,
  ImagePlus,
  Loader2,
  Mic,
  Plus,
  Sparkles,
  Trash2,
  Type,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { DesignToolImportModal } from '../media/DesignToolImportModal';
import { useDesignTools } from '../media/useDesignTools';
import { useProxyImage } from '../../lib/useProxyImage';
import { PlayBadge } from '../media/MediaThumb';
import { cn } from '../../lib/cn';
import { AlertBanner } from '../../components/ui/AlertBanner';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Field';
import { useDialogs } from '../../components/ui/DialogProvider';
import {
  addDocumentBlocked,
  documentFields,
  groupBlocks,
  MEDIA_KINDS,
  panelRuleViolation,
  wouldMixMedia,
  moveBlockBefore,
  newBlockKey,
  newDocumentBlock,
  newImageBlock,
  newTextBlock,
  withoutEmptyDocuments,
  withoutEmptyMedia,
  type AttachedAsset,
  type BlockGroup,
  type EditorBlock,
} from './blocks';
import { CharCounter, IconButton } from './EditorControls';
import {
  addThreadPost,
  canStartThread,
  insertMediaAtAnchor,
  isThreadForm,
  mediaSlotFor,
  moveThreadPost,
  patchBlockAt,
  removeThreadPost,
  startThread,
  threadPosts,
  withPostBlocks,
  type ThreadPostView,
} from './thread';
import { ADD_POST_FOCUS, ThreadEditor } from './ThreadEditor';
import { OVER_LIMIT_BORDER } from './ThreadPostChanges';
import { useUploadCounts, type UploadCounts } from './uploadHold';

export type { AttachedAsset };

interface ContentBlocksEditorProps {
  blocks: EditorBlock[];
  setBlocks: Dispatch<SetStateAction<EditorBlock[]>>;
  focusedKey: string | null;
  onFocusBlock: (key: string) => void;
  readOnly: boolean;
  /**
   * The destination's hard character cap, shown as a counter on every TEXT piece. Null for a
   * destination that doesn't cap length (Blog/Video/Podcast, or an uncapped platform) — then no
   * counter is rendered at all, rather than a limit the platform doesn't actually enforce.
   */
  charLimit: number | null;
  /**
   * How many images this post may carry in total (X 4, everywhere else 20). Enforced HERE, at
   * compose time, so a gallery the destination would refuse can never be built in the first place.
   */
  imageLimit: number;
  /**
   * The destination this post is going to, or null for a non-social channel. Drives the per-platform
   * composition rules — how many media items, whether photos and video may mix, and whether the
   * destination composes as one caption and one media field rather than a sequence of blocks.
   */
  platform: SocialPlatform | null;
  /**
   * Open the Media Library in the composer's sidebar slot, naming the gallery that asked by the
   * key of its first block. A KEY, not a closure: the panel stays open while the post keeps
   * changing, and the insert must be resolved against the block list as it is THEN. Absent → the
   * button is hidden (a caller with no sidebar to swap).
   */
  onRequestMediaLibrary?: (galleryAnchorKey: string) => void;
  /**
   * Open Generate media for the gallery named by the key of its first block, a key for the same
   * reason as the library's. Absent → no "Generate media" button.
   */
  onRequestGenerateMedia?: (galleryAnchorKey: string) => void;
  /**
   * Open the Media Library to choose a PDF for the PDF panel `documentKey`. A key, for the same
   * reason as the gallery's. Absent → the panel offers an upload only.
   */
  onRequestDocumentLibrary?: (documentKey: string) => void;
  /**
   * Told as each upload into the post starts (`+1`) and as it ends (`-1`), under the key of the first
   * block of its box - a photo, a video, a PDF or an imported design alike. Until a file lands its
   * slot is empty, and a Save or Publish in that window would go out without it - so the composer
   * holds them (uploadHold.ts). Told from the upload's own start and end, never from a box being
   * drawn or taken away: the editor is not drawn on the composer's other tabs, and an upload started
   * here still lands while one of them is open.
   */
  onUploadingChange?: (anchorKey: string, delta: 1 | -1) => void;
  /**
   * The count the composer keeps from those reports. Given, every box reads it, so a box drawn by an
   * editor mounted again - back from another tab - still shows an upload that is on its way; absent,
   * the editor counts for itself.
   */
  uploadsAt?: UploadCounts;
  /**
   * "Apply Same Media to Set?" on the media panel. Absent → no button: a post that is not one of a
   * set of destinations has nothing to apply its media to.
   */
  mediaToSet?: MediaToSetControls;
  /**
   * Whether this is a SOCIAL post, whatever its destination. A social post is one text box and one
   * media panel even before its account is chosen — the rule is the same on every platform now, so
   * waiting for `platform` let a fresh post grow extra boxes in a workspace with nothing connected.
   */
  social?: boolean;
  /**
   * A save, publish or AI call is in flight. The shape of a thread - adding, moving and deleting
   * its posts - is frozen until it is back: what is being saved is the form as it was sent.
   */
  busy?: boolean;
  /**
   * A publish is in flight. A thread is being sent from what was just saved, so its text boxes and
   * media boxes cannot change until it is back: a post typed into now could come back live.
   */
  sending?: boolean;
  /**
   * Drawn right under the text box of a single SOCIAL post - above its media box, or above the
   * buttons when it has none: the composer's AI "Convert to thread" card. Never drawn in a thread,
   * nor on a post that is not social.
   */
  afterText?: ReactNode;
  /**
   * An AI conversion of the single post's text into a thread is on its way. Until it is back the
   * text box is read-only on a grey ground (still readable, still selectable), it cannot be cleared,
   * and "Add media" and "Add post to thread" wait: what was sent is what comes back. A media box
   * already open is left as it is.
   */
  converting?: boolean;
  /**
   * "Show changes" after an AI conversion is on: by the key of each post's text block, the AI's
   * changes to show in it, or null (or no entry) to show its text plain. Every post's text is
   * read-only meanwhile. A thread only; absent, every post has its text box.
   */
  changesShown?: ReadonlyMap<string, ThreadConversionChange[] | null>;
}

/** What the media panel needs to offer "Apply Same Media to Set?" and report how it went. */
export interface MediaToSetControls {
  /** Save the post, then apply the panel's media, in the author's order. */
  onApply: (assetIds: string[]) => void;
  /** The save and the apply after it, from the click until the apply is back. */
  pending: boolean;
  /**
   * The apply cannot start: it saves the post first, so it waits whenever Save does - while the
   * post cannot be saved as it stands (`heldReason`), and while another save, a publish or an AI
   * draft is away, when its own save would run beside that one, each sending the rows the other is
   * sending.
   */
  held?: boolean;
  /**
   * Why the post cannot be saved as it stands - the reason Save gives - or null. Said under the
   * button, and as its description, for as long as it holds: it is worked out from the form each
   * time it is drawn, so it goes the moment its cause does. A click that could only be refused is
   * not offered; a refusal written to the composer's error line stayed there after its cause had
   * gone, saying something no longer true.
   */
  heldReason?: string | null;
  /**
   * The outcome, and exactly which media it applied. Shown above the media panel only while the
   * panel still holds that media: once the author changes it, "added to all posts in set" would be
   * a claim about media the other posts do not have.
   */
  notice: { assetIds: string[]; result: ApplyMediaToSetResult } | null;
}

const KIND_LABEL: Record<ContentKind, string> = {
  [ContentKind.TEXT]: 'Text',
  [ContentKind.IMAGE]: 'Image',
  [ContentKind.AUDIO]: 'Audio',
  [ContentKind.VIDEO]: 'Video',
  [ContentKind.DOCUMENT]: 'PDF carousel',
};

/** A control that is disabled cannot take focus; a read-only "Show changes" view never is. */
const isDisabled = (element: HTMLElement) =>
  'disabled' in element && Boolean((element as HTMLButtonElement).disabled);

/**
 * The ordered list of Content pieces that make up a Post. TEXT blocks are fully
 * editable (with the composer's AI assist targeting the focused one). IMAGE/AUDIO/
 * VIDEO blocks upload to the tenant asset library (upload → sets assetId, previews
 * the auth-proxied blob) and persist identically — kind + assetId per piece.
 */
export function ContentBlocksEditor({
  blocks,
  setBlocks,
  focusedKey,
  onFocusBlock,
  readOnly,
  charLimit,
  imageLimit,
  platform,
  onRequestMediaLibrary,
  onRequestGenerateMedia,
  onRequestDocumentLibrary,
  onUploadingChange,
  uploadsAt: hostUploads,
  mediaToSet,
  social = platform != null,
  busy = false,
  sending = false,
  afterText,
  converting = false,
  changesShown,
}: ContentBlocksEditorProps) {
  const patchBlock = (key: string, patch: Partial<EditorBlock>) =>
    setBlocks((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  /**
   * A media box's lone video or photo, changed by its own upload. Found in `prev`, by key, where
   * the file lands as the post is NOW - the same in both layouts, since the upload may finish after
   * the post became a thread, went back to one post, or was locked.
   */
  const patchMedia = (key: string, patch: Partial<EditorBlock>) =>
    setBlocks((prev) => patchBlockAt(prev, key, patch));

  const removeBlock = (key: string) => setBlocks((prev) => prev.filter((b) => b.key !== key));

  const addBlock = (block: EditorBlock) => {
    setBlocks((prev) => [...prev, block]);
    onFocusBlock(block.key);
  };

  /**
   * Consecutive IMAGE pieces render as ONE "Images" block — a gallery is a run of image Contents,
   * and the wireframe shows them as tiles under a single header. Grouping is a RENDERING concern
   * only: each tile stays its own Content row, so ordering, persistence and the publish path (which
   * reads every IMAGE content in `order`) are untouched. Only CONSECUTIVE images group, so
   * image/text/image stays two galleries either side of the text rather than silently reordering.
   */
  const mergeMedia = social || platform != null;
  /**
   * An add was refused for mixing photos and video; said above the media panel until the next add.
   * Holds the key of the gallery that refused it, so a thread says it above that post's box alone.
   */
  const [mixRefused, setMixRefused] = useState<string | null>(null);
  /**
   * Uploads still on their way, counted by the key of their box's first block. Kept out of the box:
   * the box is drawn afresh when the post becomes a thread, or one post again, and an upload it
   * started has to show as one there too, until its file lands. The composer's count when it keeps
   * one (`uploadsAt`), which outlives this editor; else this editor's own. Every start and end is
   * also told to the composer (`onUploadingChange`).
   */
  const ownUploads = useUploadCounts();
  const uploadsAt = hostUploads ?? ownUploads.counts;
  const countUpload = (anchorKey: string, delta: 1 | -1) => {
    ownUploads.report(anchorKey, delta);
    onUploadingChange?.(anchorKey, delta);
  };
  const groups = groupBlocks(blocks, mergeMedia);

  /**
   * Whether one more of each kind would break the destination's rules — asked of the same function
   * the save and publish gates ask, so a button that stays enabled can never produce a post those
   * gates then refuse.
   */
  const addTextBlocked = panelRuleViolation(blocks, platform, social, 'text');
  // A social post holding a PDF takes nothing beside it - said in those words, not as a rule about
  // fields. An EMPTY PDF slot doesn't block: adding media replaces it (and a Blog/Video/Podcast post
  // is not held to LinkedIn's rule at all).
  const hasDocument = mergeMedia && blocks.some((b) => b.kind === ContentKind.DOCUMENT && b.assetId);
  const addMediaBlocked = hasDocument
    ? DOCUMENT_EXCLUSIVE_MESSAGE
    : panelRuleViolation(withoutEmptyDocuments(blocks), platform, social, 'media');
  /**
   * A PDF - shown on LinkedIn as a swipeable carousel - is offered on a LinkedIn post only: no other
   * destination here can publish one. Keyed on the destination the composer resolved, which is null
   * until an account is known, so a workspace with nothing connected is never offered it.
   */
  const offersDocument = platform === SocialPlatform.LINKEDIN;
  /**
   * On a social post "Add media" opens the media panel, so it goes once the panel is there - an
   * empty tile included: the panel is visible, and it is where media is added.
   */
  const offersMedia = !social || !blocks.some((b) => MEDIA_KINDS.has(b.kind));
  const documentBlocked =
    addDocumentBlocked(blocks) ?? panelRuleViolation(withoutEmptyMedia(blocks), platform, social, 'media');

  /** Add a block in place of the empty one of the OTHER media kind, which it replaces. */
  const addReplacing = (block: EditorBlock, drop: (prev: EditorBlock[]) => EditorBlock[]) => {
    setBlocks((prev) => [...drop(prev), block]);
    onFocusBlock(block.key);
  };

  /** Move a whole group past its neighbour, so a gallery reorders as one unit. */
  const moveGroup = (groupIndex: number, delta: number) =>
    setBlocks((prev) => {
      // Same grouping the render uses, or a move would reorder units the author cannot see.
      const current = groupBlocks(prev, mergeMedia);
      const j = groupIndex + delta;
      if (groupIndex < 0 || j < 0 || j >= current.length) return prev;
      const next = [...current];
      [next[groupIndex], next[j]] = [next[j], next[groupIndex]];
      return next.flatMap((g) => g.blocks);
    });

  const removeGroup = (group: BlockGroup) => {
    const keys = new Set(group.blocks.map((b) => b.key));
    setBlocks((prev) => prev.filter((b) => !keys.has(b.key)));
  };

  /** Total images ALREADY on the post — the cap is per post (the whole gallery ships together). */
  const imageCount = blocks.filter((b) => b.kind === ContentKind.IMAGE && b.assetId).length;
  const endsWithGallery = groups[groups.length - 1]?.kind === ContentKind.IMAGE;

  /** Attach assets to the gallery anchored at `anchorKey`. The helper re-derives the run and
   *  enforces the cap from the LIVE block list, so a stale caller can't misplace or overshoot. */
  const addImages = (anchorKey: string, assets: AttachedAsset[]) => {
    // No social destination takes photos and video together, so an add that would mix them is
    // refused here, where it was attempted, rather than accepted and stopped later at publishing.
    // In a thread that is asked of the gallery's own post: each post is published on its own.
    if (mergeMedia && wouldMixMedia(mediaSlotFor(blocks, anchorKey, imageLimit).held, assets)) {
      setMixRefused(anchorKey);
      return;
    }
    setMixRefused(null);
    // Found in `prev`, where the files land as the post is NOW: an upload that finishes after the
    // post became a thread, or after a thread became one post again, joins the post that holds
    // the gallery; a gallery that is gone takes nothing.
    setBlocks((prev) =>
      insertMediaAtAnchor(prev, anchorKey, assets, imageLimit, {
        social: mergeMedia,
        recheckMixing: false,
      }),
    );
  };

  /**
   * Where focus goes after an action that moves or removes what had it: the text box of a post
   * just added, the arrow of a card that moved, the text box of the post that took a deleted one's
   * place. Elements register under a name - a TEXT block's key, a post's key with ':up' / ':down'
   * / ':delete' - and an action names the ones to try, in order: the first that is drawn and
   * enabled after that render takes focus. Only an action asks: opening a post focuses nothing.
   */
  const focusables = useRef(new Map<string, HTMLElement>());
  const focusTarget = (name: string) => (element: HTMLElement | null) => {
    if (element) focusables.current.set(name, element);
    else focusables.current.delete(name);
  };
  const [focusRequest, setFocusRequest] = useState<{ names: string[] } | null>(null);
  const focusOn = (...names: Array<string | undefined>) =>
    setFocusRequest({ names: names.filter((name): name is string => name != null) });
  useEffect(() => {
    if (!focusRequest) return;
    for (const name of focusRequest.names) {
      const element = focusables.current.get(name);
      if (element && !isDisabled(element)) {
        element.focus();
        return;
      }
    }
  }, [focusRequest]);

  /**
   * The card under the single post's text box (`afterText`) went, taking focus with it ("Keep as a
   * single post"), or came while focus was nowhere ("Undo" took the thread and its banner away):
   * focus goes to the text box. A card that puts focus on a button of its own has done so by now
   * (its effect runs before this one), and focus anywhere else stays where it is.
   */
  const hasAfterText = social && afterText != null && typeof afterText !== 'boolean';
  const hadAfterText = useRef(hasAfterText);
  const singleTextKey =
    social && !isThreadForm(blocks)
      ? blocks.find((b) => b.kind === ContentKind.TEXT)?.key
      : undefined;
  useEffect(() => {
    if (hadAfterText.current === hasAfterText) return;
    hadAfterText.current = hasAfterText;
    const active = document.activeElement;
    if (active != null && active !== document.body) return;
    const box = singleTextKey != null ? focusables.current.get(singleTextKey) : undefined;
    if (box && !isDisabled(box)) box.focus();
  }, [hasAfterText, singleTextKey]);

  /**
   * "Add post to thread" on a single post: what is there becomes post 1, untouched, and an empty
   * post 2 follows it, in focus. Keys are made here, not in the updater, which runs twice under
   * StrictMode; the updater asks again whether the post can start a thread, as it is NOW.
   */
  const offersThread = social && supportsThreads(platform);
  const cannotStartThread = canStartThread(blocks);
  const beginThread = () => {
    const firstKey = newBlockKey();
    const second = { ...newTextBlock(), threadKey: newBlockKey() };
    setBlocks((prev) => (canStartThread(prev) ? prev : startThread(prev, firstKey, second)));
    onFocusBlock(second.key);
    focusOn(second.key);
  };

  if (social && isThreadForm(blocks)) {
    /**
     * Every change to a post's blocks goes through `withPostBlocks`: it gives each block the post's
     * key, and leaves a locked post as it is.
     */
    const inPost = (postKey: string, update: (slice: EditorBlock[]) => EditorBlock[]) =>
      setBlocks((prev) => withPostBlocks(prev, postKey, update));
    const posts = threadPosts(blocks);
    const textKeyOf = (post: ThreadPostView | undefined) =>
      post?.blocks.find((b) => b.kind === ContentKind.TEXT)?.key;

    const addPost = () => {
      const post = { ...newTextBlock(), threadKey: newBlockKey() };
      setBlocks((prev) => addThreadPost(prev, post));
      onFocusBlock(post.key);
      focusOn(post.key);
    };
    const movePost = (postKey: string, delta: -1 | 1) => {
      setBlocks((prev) => moveThreadPost(prev, postKey, delta));
      // The same arrow on the card that moved while it can still be pressed, else its other one.
      const [same, other] = delta < 0 ? ['up', 'down'] : ['down', 'up'];
      focusOn(`${postKey}:${same}`, `${postKey}:${other}`);
    };
    const deletePost = (postKey: string) => {
      const at = posts.findIndex((p) => p.key === postKey);
      setBlocks((prev) => removeThreadPost(prev, postKey));
      // The post that took its place, else the new last one - after a collapse, that is the single
      // post's text box, the same block. A locked post's box cannot take focus: then the add
      // button.
      focusOn(textKeyOf(posts[at + 1]), textKeyOf(posts[at - 1]), ADD_POST_FOCUS);
    };
    const addPostMedia = (postKey: string) => {
      const tile = newImageBlock();
      inPost(postKey, (slice) => [...slice, tile]);
      onFocusBlock(tile.key);
      // "Add media" goes once the box is open: focus moves into the box.
      focusOn(tile.key);
    };
    /**
     * A post's media box: the same box as a single post's, fed with this post's media and counted
     * against this post alone. Every removal in it takes the block away, so a card never keeps an
     * empty tile once its file is gone; it has no arrows and no "Apply Same Media to Set?". Drawn
     * read-only on a locked post and while the thread is being sent.
     */
    const postMedia = (post: ThreadPostView) => {
      const media = post.blocks.filter((b) => MEDIA_KINDS.has(b.kind));
      if (media.length === 0) return null;
      const boxReadOnly = readOnly || post.lock != null || sending;
      const remove = (key: string) =>
        inPost(post.key, (slice) => slice.filter((b) => b.key !== key));
      return (
        <MediaBox
          group={groupBlocks(media, true)[0]}
          mergeMedia
          focusRef={focusTarget(media[0].key)}
          uploading={uploadsAt.has(media[0].key)}
          onUploadCount={countUpload}
          focused={media.some((b) => b.key === focusedKey)}
          readOnly={boxReadOnly}
          imageCount={media.filter((b) => b.kind === ContentKind.IMAGE && b.assetId).length}
          imageLimit={imageLimit}
          mixRefused={mixRefused != null && media.some((b) => b.key === mixRefused)}
          controls={
            !boxReadOnly && (
              <div className="flex items-center gap-0.5">
                <IconButton
                  label="Remove piece"
                  onClick={() =>
                    inPost(post.key, (slice) => slice.filter((b) => !MEDIA_KINDS.has(b.kind)))
                  }
                  danger
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </IconButton>
              </div>
            )
          }
          onAddImages={addImages}
          onRequestMediaLibrary={onRequestMediaLibrary}
          onRequestGenerateMedia={onRequestGenerateMedia}
          onRemoveImage={remove}
          onReorder={(fromKey, beforeKey) =>
            inPost(post.key, (slice) => moveBlockBefore(slice, fromKey, beforeKey))
          }
          onChange={patchMedia}
          onRemoveMedia={remove}
        />
      );
    };

    return (
      <div className="space-y-3">
        <ThreadEditor
          posts={posts}
          platform={platform}
          charLimit={charLimit}
          readOnly={readOnly}
          busy={busy}
          sending={sending}
          focusTarget={focusTarget}
          onFocusText={onFocusBlock}
          onTextChange={(postKey, key, text) =>
            inPost(postKey, (slice) => slice.map((b) => (b.key === key ? { ...b, text } : b)))
          }
          onMove={movePost}
          onDelete={deletePost}
          onDeleteCancelled={(postKey) => focusOn(`${postKey}:delete`)}
          onAddMedia={addPostMedia}
          onAddPost={addPost}
          media={postMedia}
          changesShown={changesShown}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {blocks.length === 0 && (
        <p className="rounded-lg border border-dashed border-line-dashed bg-page/50 px-3 py-6 text-center text-sm text-ink-faint">
          No content yet. Add a text, image, audio, or video piece below.
        </p>
      )}

      {groups.map((group, groupIndex) => {
        const first = group.blocks[0];
        const focused = group.blocks.some((b) => b.key === focusedKey);
        // A social post always keeps a text box - there is no "Add text" to bring one back - so the
        // trash of its LAST one empties it instead. Beside another box (a Blog draft switched to
        // Social) a box still goes, until one is left.
        const clearsText =
          social &&
          group.kind === ContentKind.TEXT &&
          !blocks.some((b) => b.kind === ContentKind.TEXT && b.key !== first.key);
        const controls = !readOnly && (
          <div className="flex items-center gap-0.5">
            {/* A social post is one text box and one media panel, in order: nothing to move. */}
            {!social && (
              <>
                <IconButton
                  label="Move down"
                  disabled={groupIndex === groups.length - 1}
                  onClick={() => moveGroup(groupIndex, 1)}
                >
                  <ArrowDown className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton
                  label="Move up"
                  disabled={groupIndex === 0}
                  onClick={() => moveGroup(groupIndex, -1)}
                >
                  <ArrowUp className="h-4 w-4" aria-hidden />
                </IconButton>
              </>
            )}
            {clearsText ? (
              <IconButton
                label="Clear text"
                // Read-only while a conversion is away: what was sent is what comes back.
                disabled={first.text === '' || converting}
                onClick={() => patchBlock(first.key, { text: '' })}
                danger
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </IconButton>
            ) : (
              <IconButton label="Remove piece" onClick={() => removeGroup(group)} danger>
                <Trash2 className="h-4 w-4" aria-hidden />
              </IconButton>
            )}
          </div>
        );
        if (MEDIA_KINDS.has(group.kind)) {
          return (
            <MediaBox
              key={group.key}
              group={group}
              mergeMedia={mergeMedia}
              focusRef={focusTarget(group.key)}
              uploading={uploadsAt.has(group.key)}
              onUploadCount={countUpload}
              focused={focused}
              readOnly={readOnly}
              imageCount={imageCount}
              imageLimit={imageLimit}
              mixRefused={mixRefused != null}
              mediaToSet={mediaToSet}
              controls={controls}
              onAddImages={addImages}
              onRequestMediaLibrary={onRequestMediaLibrary}
              onRequestGenerateMedia={onRequestGenerateMedia}
              onRemoveImage={(key) =>
                group.blocks.length === 1
                  ? patchBlock(key, {
                      assetId: null,
                      assetUrl: null,
                      assetMimeType: null,
                      assetPosterUrl: null,
                    })
                  : removeBlock(key)
              }
              // Re-derived from `prev` inside the updater, never from a captured snapshot — the
              // same rule the other gallery mutations follow.
              onReorder={(fromKey, beforeKey) =>
                setBlocks((prev) => moveBlockBefore(prev, fromKey, beforeKey))
              }
              onChange={patchMedia}
            />
          );
        }
        // Length is only meaningful for text, and only where the destination caps it.
        const textCount =
          charLimit != null && group.kind === ContentKind.TEXT
            ? countPostChars(platform, first.text)
            : null;
        // Over the cap on X or Threads, the text box is red as well as its count - while it can be
        // shortened. Only then: on LinkedIn, Instagram and Facebook only the count turns red, and
        // there, as on a post with no cap (Blog, Video, Podcast), the box is drawn as it always was.
        const textOver =
          textCount != null &&
          charLimit != null &&
          textCount > charLimit &&
          !readOnly &&
          supportsThreads(platform);
        // While a conversion is away the text box is read-only, on the grey of a box that cannot
        // be typed in - but not disabled, so it stays readable at full contrast, and selectable.
        const textHeld = converting && group.kind === ContentKind.TEXT;
        // One Fragment per group, card or not, so the panel is never drawn afresh - and its text
        // box never loses focus - when the card comes or goes under it.
        return (
          <Fragment key={group.key}>
            <Panel
              kind={group.kind}
              label={KIND_LABEL[group.kind]}
              focused={focused}
              controls={
                <>
                  {/* Over the cap the count goes red and the composer blocks Save/Publish — the
                      platform would reject the post anyway, so failing here beats failing at
                      delivery. */}
                  {textCount != null && charLimit != null && (
                    <CharCounter length={textCount} limit={charLimit} platform={platform} />
                  )}
                  {controls}
                </>
              }
            >
              <div className="p-3">
                {group.kind === ContentKind.TEXT ? (
                  <Textarea
                    ref={focusTarget(first.key)}
                    value={first.text}
                    onChange={(e) => patchBlock(first.key, { text: e.target.value })}
                    onFocus={() => onFocusBlock(first.key)}
                    rows={8}
                    disabled={readOnly}
                    placeholder="Write here, or generate a draft with AI →"
                    readOnly={textHeld || undefined}
                    aria-invalid={textOver || undefined}
                    className={
                      cn(textOver && OVER_LIMIT_BORDER, textHeld && 'read-only:bg-page') || undefined
                    }
                  />
                ) : group.kind === ContentKind.DOCUMENT ? (
                  <DocumentBlockBody
                    block={first}
                    readOnly={readOnly}
                    onChange={(patch) => patchBlock(first.key, patch)}
                    onRequestLibrary={
                      onRequestDocumentLibrary ? () => onRequestDocumentLibrary(first.key) : undefined
                    }
                    uploading={uploadsAt.has(first.key)}
                    onUploadCount={countUpload}
                  />
                ) : (
                  <MediaBlockBody
                    block={first}
                    readOnly={readOnly}
                    onChange={(patch) => patchBlock(first.key, patch)}
                    uploading={uploadsAt.has(first.key)}
                    onUploadCount={countUpload}
                  />
                )}
              </div>
            </Panel>
            {/* The first text box's alone: a social post has one. */}
            {hasAfterText && first.key === singleTextKey && afterText}
          </Fragment>
        );
      })}

      {/* No toolbar at all when it would hold no button. */}
      {!readOnly && (!social || offersMedia || offersDocument || offersThread) && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {/* A social post is one text box, always there: nothing to add. */}
          {!social && (
            <Button
              variant="secondary"
              size="sm"
              disabled={Boolean(addTextBlocked)}
              title={addTextBlocked ?? undefined}
              onClick={() => addBlock(newTextBlock())}
            >
              <Type className="h-4 w-4" aria-hidden />
              Add text
            </Button>
          )}
          {/*
            One media button instead of three. The old Add video / Add audio buttons did nothing but
            drop an empty block whose only source was a local file — no library, no design tool — so
            the toolbar offered three doors to one room and only one of them was furnished.

            The block this creates takes images from the library, a design tool, or an upload, and a
            video from the library or an upload; a video lands as its own block rather than joining
            the image gallery. Existing video and audio blocks are untouched: their bodies still
            render, still accept a file, and still save. Only the way to create an empty one is gone.
          */}
          {offersMedia && (
            <Button
              variant="secondary"
              size="sm"
              // Disabled when the post already ENDS in a gallery: a second empty image block would
              // just join that run and render nothing, so the click would look broken. Add to the
              // gallery that's already there. And while a conversion is away.
              disabled={
                imageCount >= imageLimit ||
                endsWithGallery ||
                Boolean(addMediaBlocked) ||
                converting
              }
              title={
                imageCount >= imageLimit
                  ? `This post already has the maximum of ${imageLimit} images.`
                  : endsWithGallery
                    ? 'Add media to the gallery below.'
                    : (addMediaBlocked ?? undefined)
              }
              onClick={() => {
                const tile = newImageBlock();
                addReplacing(tile, withoutEmptyDocuments);
                // On a social post the button goes once the box is there: focus moves into the box
                // it opened, rather than falling to the page.
                if (social) focusOn(tile.key);
              }}
            >
              <ImagePlus className="h-4 w-4" aria-hidden />
              Add media
            </Button>
          )}
          {offersDocument && (
            <Button
              variant="secondary"
              size="sm"
              disabled={Boolean(documentBlocked)}
              title={documentBlocked ?? 'A multi-page PDF, shown on LinkedIn as a swipeable carousel'}
              onClick={() => addReplacing(newDocumentBlock(), withoutEmptyMedia)}
            >
              <FileText className="h-4 w-4" aria-hidden />
              Add PDF carousel
            </Button>
          )}
          {offersThread && (
            <Button
              variant="outline"
              size="sm"
              disabled={cannotStartThread != null || busy || converting}
              title={cannotStartThread ?? undefined}
              onClick={beginThread}
            >
              <Plus className="h-4 w-4" aria-hidden />
              Add post to thread
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Same media, same order — the only state in which an apply's outcome still describes the panel. */
const sameIds = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

const MEDIA_SET_REVIEW =
  'Please review each post, as each platform has different restrictions on media';

/**
 * How "Apply Same Media to Set?" went, above the panel it came from.
 *
 * The words the design asks for when every sibling took the media. When one could not — already
 * approved, scheduled or published — it says which and why instead: "added to all posts in set"
 * over a post that did not change is a confident lie someone discovers on the day it goes out.
 */
export function MediaToSetNotice({ result }: { result: ApplyMediaToSetResult }) {
  const applied = result.outcomes.filter((o) => o.applied);
  const refused = result.outcomes.filter((o) => !o.applied);

  const name = (o: (typeof result.outcomes)[number]) =>
    `${o.platform ? socialPlatformLabel[o.platform] : 'One destination'}${o.accountName ? ` · ${o.accountName}` : ''}`;
  if (result.outcomes.length === 0) {
    return <AlertBanner tone="info">There are no other posts in this set to add media to.</AlertBanner>;
  }
  if (refused.length === 0) {
    return (
      <AlertBanner tone="info">
        Media has been added to all posts in set. {MEDIA_SET_REVIEW}
      </AlertBanner>
    );
  }
  return (
    <AlertBanner tone="warning">
      {applied.length === 0
        ? 'Media was not added to any other post in this set.'
        : `Media has been added to ${applied.length} of ${result.outcomes.length} posts in set. ${MEDIA_SET_REVIEW}`}
      {refused.map((o) => (
        <span key={o.postId} className="mt-1 block">
          {name(o)}: {o.reason ?? 'could not be changed'}
        </span>
      ))}
    </AlertBanner>
  );
}


/** One panel of the editor: the bordered box, its header (icon, name, controls), its body. */
function Panel({
  kind,
  label,
  focused,
  controls,
  note,
  children,
}: {
  kind: ContentKind;
  label: string;
  focused: boolean;
  controls: ReactNode;
  /** A line under the header, about one of its controls - why it waits - or nothing. */
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border bg-card',
        focused ? 'border-ink-ghost ring-1 ring-line' : 'border-line',
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b border-line-soft px-3 py-1.5">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
          <BlockKindIcon kind={kind} />
          {label}
        </span>
        <div className="flex items-center gap-2">{controls}</div>
      </div>
      {note}
      {children}
    </div>
  );
}

/**
 * The media box: the panel that holds a post's photos and video. Its header - the name, the "N of
 * limit" counter, "Apply Same Media to Set?" and the header's buttons - and its body, the gallery
 * or a lone video. The single-post layout draws one for each media group; a card of a thread draws
 * one for its post, fed with that post's blocks and that post's image count. One component, so the
 * box is the same box in both.
 */
function MediaBox({
  group,
  mergeMedia,
  focusRef,
  uploading,
  onUploadCount,
  focused,
  readOnly,
  imageCount,
  imageLimit,
  mixRefused,
  mediaToSet,
  controls,
  onAddImages,
  onRequestMediaLibrary,
  onRequestGenerateMedia,
  onRemoveImage,
  onReorder,
  onChange,
  onRemoveMedia,
}: {
  group: BlockGroup;
  mergeMedia: boolean;
  /**
   * Registers the box's first control - a gallery's drop zone - for the editor to put focus on
   * when "Add media" opens the box and goes.
   */
  focusRef?: (element: HTMLButtonElement | null) => void;
  /**
   * An upload into this box is still on its way, counted by the editor under the box's first block
   * (`onUploadCount`, told as each upload starts and ends) - so a box drawn afresh, when the post
   * becomes a thread or one post again, still shows the upload the box before it started.
   */
  uploading?: boolean;
  onUploadCount?: (anchorKey: string, delta: 1 | -1) => void;
  focused: boolean;
  readOnly: boolean;
  /** The images counted against the cap: the post's - in a thread, this thread post's. */
  imageCount: number;
  imageLimit: number;
  /** An add to this box was refused for mixing photos and video. */
  mixRefused: boolean;
  mediaToSet?: MediaToSetControls;
  /** The header's buttons, after the counter. */
  controls: ReactNode;
  onAddImages: (anchorKey: string, assets: AttachedAsset[]) => void;
  onRequestMediaLibrary?: (galleryAnchorKey: string) => void;
  onRequestGenerateMedia?: (galleryAnchorKey: string) => void;
  onRemoveImage: (key: string) => void;
  /** Move one image before another; `null` target means past the last one. */
  onReorder: (fromKey: string, beforeKey: string | null) => void;
  onChange: (key: string, patch: Partial<EditorBlock>) => void;
  /** Take a lone video away, not empty it. Absent: "Remove video" empties it, as it always has. */
  onRemoveMedia?: (key: string) => void;
}) {
  // A merged panel holding more than one piece always draws as a gallery, videos included. The
  // single-piece body shows only its FIRST block, so two videos in one panel hid the second —
  // and removing the visible one left the hidden one to publish.
  const isImages =
    group.kind === ContentKind.IMAGE ||
    (mergeMedia && MEDIA_KINDS.has(group.kind) && group.blocks.length > 1);
  // One panel holding both kinds is "Media"; an image-only run on a non-social post keeps
  // the name it had.
  const isMediaPanel = mergeMedia && MEDIA_KINDS.has(group.kind);
  const first = group.blocks[0];
  // What "Apply Same Media to Set?" sends: the panel's attached media in the author's order.
  // An empty placeholder tile has no asset and is not media anyone chose.
  const panelAssetIds = isMediaPanel
    ? group.blocks
        .filter((b) => b.assetId && MEDIA_KINDS.has(b.kind))
        .map((b) => b.assetId as string)
    : [];
  const setNotice =
    isMediaPanel && mediaToSet?.notice && sameIds(mediaToSet.notice.assetIds, panelAssetIds)
      ? mediaToSet.notice.result
      : null;
  // Why "Apply Same Media to Set?" waits for the post to be saveable: said on the line under the
  // header that holds the button, and read out as the button's description.
  const applyWaitId = useId();
  const applyWait =
    isMediaPanel && mediaToSet && !readOnly ? (mediaToSet.heldReason ?? null) : null;
  return (
    <>
      {setNotice && <MediaToSetNotice result={setNotice} />}
      {isMediaPanel && mixRefused && (
        <AlertBanner tone="warning">{MEDIA_MIXING_MESSAGE}</AlertBanner>
      )}
      <Panel
        kind={group.kind}
        label={isMediaPanel ? 'Media' : isImages ? 'Images' : KIND_LABEL[group.kind]}
        focused={focused}
        controls={
          <>
            {isMediaPanel && mediaToSet && !readOnly && (
              <Button
                variant="secondary"
                size="sm"
                loading={mediaToSet.pending}
                disabled={panelAssetIds.length === 0 || mediaToSet.held}
                title={panelAssetIds.length === 0 ? 'Add media to this panel first.' : undefined}
                aria-describedby={applyWait ? applyWaitId : undefined}
                onClick={() => mediaToSet.onApply(panelAssetIds)}
              >
                Apply Same Media to Set?
              </Button>
            )}
            {/* Counts the WHOLE post, not this gallery: every image content ships together, so
                the cap is what the destination accepts per post. */}
            {isImages && (
              <span className="text-xs tabular-nums text-ink-faint">
                <span className={cn(imageCount > imageLimit && 'font-semibold text-red-600 dark:text-error-dark')}>
                  {imageCount}
                </span>{' '}
                of {imageLimit}
              </span>
            )}
            {controls}
          </>
        }
        note={
          applyWait ? (
            <p
              id={applyWaitId}
              className="border-b border-line-soft px-3 py-1.5 text-xs text-ink-muted"
            >
              Saves this post first. {applyWait}
            </p>
          ) : null
        }
      >
        {isImages ? (
          <ImagesBlockBody
            blocks={group.blocks}
            readOnly={readOnly}
            dropZoneRef={focusRef}
            uploading={uploading}
            onUploadCount={onUploadCount}
            remaining={Math.max(0, imageLimit - imageCount)}
            onAdd={(assets) => onAddImages(group.blocks[0].key, assets)}
            onRequestMediaLibrary={
              onRequestMediaLibrary ? () => onRequestMediaLibrary(group.blocks[0].key) : undefined
            }
            onRequestGenerateMedia={
              onRequestGenerateMedia ? () => onRequestGenerateMedia(group.blocks[0].key) : undefined
            }
            onRemoveImage={onRemoveImage}
            onReorder={onReorder}
          />
        ) : (
          <div className="p-3">
            <MediaBlockBody
              block={first}
              readOnly={readOnly}
              onChange={(patch) => onChange(first.key, patch)}
              onRemove={onRemoveMedia ? () => onRemoveMedia(first.key) : undefined}
              uploading={uploading}
              onUploadCount={onUploadCount}
            />
          </div>
        )}
      </Panel>
    </>
  );
}

/**
 * The gallery body: a persistent drag-and-drop tile followed by one square thumbnail per image, a
 * divider, then the add controls. The dropzone stays put as images are added so dragging more in is
 * always possible - until the post hits the destination's image cap, at which point every way to
 * add another is disabled rather than failing later at the platform.
 */
function ImagesBlockBody({
  blocks,
  readOnly,
  dropZoneRef,
  uploading = false,
  onUploadCount,
  remaining,
  onAdd,
  onRemoveImage,
  onRequestMediaLibrary,
  onRequestGenerateMedia,
  onReorder,
}: {
  blocks: EditorBlock[];
  readOnly: boolean;
  /** The drop zone, for the editor to put focus on. */
  dropZoneRef?: (element: HTMLButtonElement | null) => void;
  /** See {@link MediaBox}'s `uploading` and `onUploadCount`. */
  uploading?: boolean;
  onUploadCount?: (anchorKey: string, delta: 1 | -1) => void;
  remaining: number;
  onAdd: (assets: AttachedAsset[]) => void;
  onRemoveImage: (key: string) => void;
  onRequestMediaLibrary?: () => void;
  onRequestGenerateMedia?: () => void;
  /** Move one image before another; `null` target means past the last one. */
  onReorder: (fromKey: string, beforeKey: string | null) => void;
}) {
  const { alert } = useDialogs();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  /**
   * The thumbnail currently being dragged, or null when the drag is a FILE coming from the desktop.
   *
   * Both kinds of drag pass over the same container, and the upload tile calls preventDefault
   * unconditionally — which makes it a valid drop target for anything, so without this a thumbnail
   * dragged over it lights it up as if it were about to be uploaded. Every reorder target checks it.
   */
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [dropBeforeKey, setDropBeforeKey] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const { tool: designTool, label: designToolName } = useDesignTools();
  const filled = blocks.filter((b) => b.assetId);
  const atLimit = remaining <= 0;

  const upload = useMutation({
    // Counted by the editor under the gallery's first block as well (see `uploading`).
    onMutate: () => {
      const anchor = blocks[0].key;
      onUploadCount?.(anchor, 1);
      return anchor;
    },
    mutationFn: async (files: File[]) => {
      // `remaining` is an IMAGE budget. Video does not join the gallery and is not counted against
      // the platform's image cap (appendMediaToGallery splits it out), so it must not spend a slot
      // here either — clamping the mixed list would drop a chosen IMAGE to make room for a video
      // that was never going to occupy one, and then report a number that isn't true.
      const images = files.filter((f) => f.type.startsWith('image/'));
      const videos = files.filter((f) => !f.type.startsWith('image/'));
      const acceptedImages = images.slice(0, remaining);
      const assets: AttachedAsset[] = [];
      for (const file of [...acceptedImages, ...videos]) {
        const asset = await api.uploadAsset(file);
        assets.push({
          id: asset.id,
          blobUrl: asset.blobUrl,
          mimeType: asset.mimeType,
          posterUrl: asset.posterUrl,
              thumbUrl: asset.thumbUrl,
        });
      }
      return {
        assets,
        skipped: images.length - acceptedImages.length,
        allowed: acceptedImages.length,
      };
    },
    onSuccess: ({ assets, skipped, allowed }) => {
      onAdd(assets);
      if (skipped > 0) {
        void alert({
          title: 'Some images were not added',
          message:
            'This post could take ' +
            allowed +
            ' more image' +
            (allowed === 1 ? '' : 's') +
            ', so ' +
            skipped +
            ' of the images you chose were skipped.',
        });
      }
    },
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: err instanceof ApiRequestError ? err.message : 'Could not upload the files.',
      }),
    onSettled: (_result, _error, _files, anchor) => {
      if (anchor) onUploadCount?.(anchor, -1);
    },
  });
  const pending = upload.isPending || uploading;

  const take = (list: FileList | null) => {
    // Only what a platform will actually publish. This was an `image/`-or-`video/` prefix test, which
    // also matched `image/svg+xml` and every video container no platform accepts — so a dragged .svg
    // became an attachment that could never be delivered.
    const all = Array.from(list ?? []);
    const files = all.filter((f) => isPostableMime(f.type));
    const rejected = all.filter((f) => !isPostableMime(f.type));
    if (files.length > 0) upload.mutate(files);
    // Rejections used to vanish without a word. That was survivable when the rule was "images and
    // video"; it is not now the rule is this specific — a .mov off a phone or a .webp off the web
    // looks exactly like an image, and a file that disappears silently reads as a broken drop target.
    if (rejected.length > 0) {
      void alert({
        title: files.length > 0 ? 'Some files were not added' : 'That file cannot go in a post',
        message: `${rejected.map((f) => f.name).join(', ')} — posts accept ${POSTABLE_MIMES_LABEL} only, because that is what the platforms take. Convert it first, or keep it in the Media Library.`,
      });
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={POSTABLE_MIMES.join(',')}
        multiple
        className="hidden"
        onChange={(e) => {
          take(e.target.files);
          e.target.value = '';
        }}
        disabled={readOnly || pending}
      />

      <div
        className="flex flex-wrap items-start gap-3 p-3"
        onDragOver={(e) => {
          // Only for a reorder — a file dragged here still belongs to the upload tile alone.
          if (!draggingKey) return;
          e.preventDefault();
          setDropBeforeKey(null);
        }}
        onDrop={(e) => {
          if (!draggingKey) return;
          e.preventDefault();
          onReorder(draggingKey, null);
          setDraggingKey(null);
          setDropBeforeKey(null);
        }}
      >
        {!readOnly && !atLimit && (
          <button
            ref={dropZoneRef}
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={pending}
            onDragOver={(e) => {
              // A thumbnail being reordered is not an upload. Leaving preventDefault unguarded made
              // this tile a legal drop target for it, so it lit up as a drop zone and the drop fell
              // through to a file handler with no files.
              if (draggingKey) return;
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              if (draggingKey) return;
              e.preventDefault();
              setDragOver(false);
              take(e.dataTransfer.files);
            }}
            className={cn(
              'focus-ring flex h-28 w-32 shrink-0 flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-2 text-center text-xs leading-tight disabled:cursor-not-allowed',
              dragOver
                ? 'border-action bg-tint text-tint-fg'
                : 'border-line-dashed text-ink-muted hover:border-ink-ghost hover:text-link',
            )}
          >
            {pending ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : (
              <Upload className="h-5 w-5" aria-hidden />
            )}
            <span>
              {pending ? (
                'Uploading...'
              ) : (
                <>
                  Drag and Drop
                  <br />
                  Media to Upload
                </>
              )}
            </span>
          </button>
        )}

        {filled.map((block, i) => (
          <ImageTile
            key={block.key}
            block={block}
            readOnly={readOnly}
            position={i}
            total={filled.length}
            dragging={draggingKey === block.key}
            dropTarget={dropBeforeKey === block.key}
            onRemove={() => onRemoveImage(block.key)}
            onDragStart={() => setDraggingKey(block.key)}
            onDragEnd={() => {
              setDraggingKey(null);
              setDropBeforeKey(null);
            }}
            onDragOverTile={() => {
              if (draggingKey && draggingKey !== block.key) setDropBeforeKey(block.key);
            }}
            onDropOnTile={() => {
              if (draggingKey && draggingKey !== block.key) onReorder(draggingKey, block.key);
              setDraggingKey(null);
              setDropBeforeKey(null);
            }}
            // Keyboard equivalent, in the same swap-by-one shape as the group arrows above. Drag is
            // not reachable without a pointer, and this gallery decides the published carousel
            // order, so it cannot be pointer-only.
            onNudge={(delta) => {
              const target = filled[i + delta];
              if (!target) return;
              onReorder(block.key, delta < 0 ? target.key : (filled[i + 2]?.key ?? null));
            }}
          />
        ))}

        {readOnly && filled.length === 0 && <p className="text-sm text-ink-faint">No images set.</p>}
      </div>

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-3 border-t border-line-soft px-3 py-2.5">
          {/* Hidden outright at the cap: there is nothing the panel could add. */}
          {onRequestMediaLibrary && !atLimit && (
            <Button size="sm" disabled={pending} onClick={onRequestMediaLibrary}>
              <ImageIcon className="h-4 w-4" aria-hidden />
              Add from Media Library
            </Button>
          )}
          {/* Sits between the library and upload buttons: same idea — bring in artwork that
              already exists — just from outside Smorgas Social. Hidden entirely when no design
              tool is configured, so it never advertises a feature the workspace can't use. */}
          {designTool && !atLimit && (
            <Button
              variant="secondary"
              size="sm"
              disabled={pending}
              onClick={() => setImportOpen(true)}
            >
              <ImageIcon className="h-4 w-4" aria-hidden />
              Add from {designToolName}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            loading={pending}
            disabled={atLimit}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-4 w-4" aria-hidden />
            Upload
          </Button>
          {/* Opens Generate media with this post as its context; "Save and return to post" puts the
              image here. Hidden at the cap, like the library button: it could only be refused. */}
          {onRequestGenerateMedia && !atLimit && (
            <Button variant="secondary" size="sm" disabled={pending} onClick={onRequestGenerateMedia}>
              <Sparkles className="h-4 w-4" aria-hidden />
              Generate media
            </Button>
          )}
          <span className="text-xs text-ink-faint">
            {atLimit ? 'Image limit reached' : 'Up to 100MB'}
          </span>
        </div>
      )}

      {designTool && (
        <DesignToolImportModal
          tool={designTool}
          open={importOpen}
          onClose={() => setImportOpen(false)}
          // On its way into the gallery like an upload, and counted as one under the gallery's first
          // block as it is NOW: the modal can be closed while the import runs, and it still lands.
          onImportStarted={() => {
            const anchor = blocks[0].key;
            onUploadCount?.(anchor, 1);
            return () => onUploadCount?.(anchor, -1);
          }}
          // The import is stored as a normal library asset, so it joins the gallery exactly as an
          // upload does — same shape, same limit handling.
          onImported={(asset) =>
            onAdd([
              {
                id: asset.id,
                blobUrl: asset.blobUrl,
                mimeType: asset.mimeType,
                posterUrl: asset.posterUrl,
              thumbUrl: asset.thumbUrl,
              },
            ])
          }
        />
      )}
    </>
  );
}

/**
 * One image in a gallery: square thumbnail, a corner remove button, and drag-to-reorder.
 *
 * Position is not decoration — array order becomes the carousel order at publish, and the first
 * image is the cover — so this also carries a keyboard path. The arrows appear on hover or focus so
 * eight thumbnails don't become sixteen permanent buttons.
 */
function ImageTile({
  block,
  readOnly,
  position,
  total,
  dragging,
  dropTarget,
  onRemove,
  onDragStart,
  onDragEnd,
  onDragOverTile,
  onDropOnTile,
  onNudge,
}: {
  block: EditorBlock;
  readOnly: boolean;
  /** Zero-based index among the RENDERED tiles, which is what the user is counting. */
  position: number;
  total: number;
  dragging: boolean;
  dropTarget: boolean;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOverTile: () => void;
  onDropOnTile: () => void;
  onNudge: (delta: -1 | 1) => void;
}) {
  // A video's blob in an `<img>` is a broken tile — that is what this row used to render for every
  // video. Its poster is the only thing here that IS an image; without one we show the film icon
  // rather than a broken picture, the same fallback the Media Library grid uses.
  const isVideo = block.kind === ContentKind.VIDEO;
  // A 112px tile, and a post can carry twenty of them. The small copy when there is one, the
  // original only as a fallback — opening a post used to download every image at full resolution.
  const preview = useProxyImage(
    isVideo ? block.assetPosterUrl : (block.assetThumbUrl ?? block.assetUrl),
  );
  const TileIcon = isVideo ? Film : ImageIcon;
  return (
    <div
      className={cn(
        'group relative h-28 w-28 shrink-0 rounded-lg transition-opacity',
        dragging && 'opacity-40',
        // A left edge marking where it will land, rather than moving the tiles around under the
        // cursor — the row wraps, so live reflow makes the drop position harder to predict.
        dropTarget && 'ring-2 ring-action ring-offset-1 ring-offset-card',
      )}
      draggable={!readOnly}
      onDragStart={(e) => {
        // Firefox starts no drag at all without data on the transfer.
        e.dataTransfer.setData('text/plain', block.key);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        if (readOnly) return;
        e.preventDefault();
        e.stopPropagation();
        onDragOverTile();
      }}
      onDrop={(e) => {
        if (readOnly) return;
        e.preventDefault();
        e.stopPropagation();
        onDropOnTile();
      }}
    >
      <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-lg border border-line bg-chip text-ink-ghost">
        {preview ? (
          <img
            src={preview}
            alt=""
            // Otherwise the browser drags the IMAGE out of the page instead of the tile.
            draggable={false}
            // White behind the picture itself, where it shows only through a transparent image's
            // clear pixels (object-cover fills the tile): dark artwork stays readable in dark mode.
            className="h-full w-full bg-white object-cover"
          />
        ) : (
          <TileIcon className="h-7 w-7" aria-hidden />
        )}
        {isVideo && preview && <PlayBadge />}
      </div>
      {!readOnly && (
        <>
          <button
            type="button"
            aria-label="Remove image"
            title="Remove image"
            onClick={onRemove}
            className="focus-ring absolute -right-1.5 -top-1.5 rounded-full border border-line-input bg-card p-0.5 text-ink-muted shadow-sm hover:bg-red-50 dark:hover:bg-error-dark-tint hover:text-red-600 dark:hover:text-error-dark"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center gap-1 p-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
            <NudgeButton
              label={`Move image ${position + 1} earlier`}
              disabled={position === 0}
              onClick={() => onNudge(-1)}
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            </NudgeButton>
            <NudgeButton
              label={`Move image ${position + 1} later`}
              disabled={position === total - 1}
              onClick={() => onNudge(1)}
            >
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </NudgeButton>
          </div>
        </>
      )}
    </div>
  );
}

/** Small overlay control on a thumbnail. Re-enables pointer events its container turns off. */
function NudgeButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="focus-ring pointer-events-auto rounded border border-line-input bg-card/95 p-0.5 text-ink-label shadow-sm hover:text-action disabled:cursor-not-allowed disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function BlockKindIcon({ kind }: { kind: ContentKind }) {
  const cls = 'h-3.5 w-3.5';
  if (kind === ContentKind.DOCUMENT) return <FileText className={cls} aria-hidden />;
  if (kind === ContentKind.IMAGE) return <ImageIcon className={cls} aria-hidden />;
  if (kind === ContentKind.VIDEO) return <Video className={cls} aria-hidden />;
  if (kind === ContentKind.AUDIO) return <Mic className={cls} aria-hidden />;
  return <Type className={cls} aria-hidden />;
}

/**
 * Media piece (IMAGE/AUDIO/VIDEO): preview the auth-proxied asset + upload/replace
 * via the asset library. The preview reuses {@link useProxyImage}, which fetches the
 * raw blob into an object URL — that URL works as an `<img>`, `<audio>`, or `<video>`
 * src alike. Upload persists as `assetId` on the block (see toCreateContentDto);
 * the server enforces the 100MB cap and allowed types.
 */
function MediaBlockBody({
  block,
  readOnly,
  onChange,
  onRemove,
  uploading = false,
  onUploadCount,
}: {
  block: EditorBlock;
  readOnly: boolean;
  onChange: (patch: Partial<EditorBlock>) => void;
  /** "Remove": take the piece away. Absent: empty it, leaving the slot to upload into. */
  onRemove?: () => void;
  /** See {@link MediaBox}'s `uploading` and `onUploadCount`. */
  uploading?: boolean;
  onUploadCount?: (anchorKey: string, delta: 1 | -1) => void;
}) {
  const { alert } = useDialogs();
  const inputRef = useRef<HTMLInputElement>(null);
  const preview = useProxyImage(block.assetThumbUrl ?? block.assetUrl);

  const isVideo = block.kind === ContentKind.VIDEO;
  const isAudio = block.kind === ContentKind.AUDIO;
  const accept = isVideo ? 'video/*' : isAudio ? 'audio/*' : 'image/*';
  const noun = isVideo ? 'video' : isAudio ? 'audio' : 'image';
  const uploadLabel = isVideo ? 'Upload a video' : isAudio ? 'Upload audio' : 'Upload an image';
  const FallbackIcon = isVideo ? Video : isAudio ? Mic : ImageIcon;

  const upload = useMutation({
    // Counted by the editor under this block as well (see `uploading`).
    onMutate: () => {
      onUploadCount?.(block.key, 1);
      return block.key;
    },
    mutationFn: (file: File) => api.uploadAsset(file),
    onSuccess: (asset) =>
      onChange({
        assetId: asset.id,
        assetUrl: asset.blobUrl,
        assetThumbUrl: asset.thumbUrl,
        assetMimeType: asset.mimeType,
        assetPosterUrl: asset.posterUrl,
      }),
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: err instanceof ApiRequestError ? err.message : `Could not upload the ${noun}.`,
      }),
    onSettled: (_asset, _error, _file, key) => {
      if (key) onUploadCount?.(key, -1);
    },
  });
  const pending = upload.isPending || uploading;

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // The picker's `accept` is a suggestion ("All files" is one click away), and the server now also
    // stores PDFs - so a video slot could otherwise end up pointing at one.
    if (!file.type.startsWith(`${noun}/`)) {
      void alert({ title: 'That file cannot go here', message: `${file.name} isn't ${noun === 'image' ? 'an image' : `a ${noun} file`}.` });
      return;
    }
    upload.mutate(file);
  };

  const actions = !readOnly && (
    <MediaActions
      noun={noun}
      pending={pending}
      layout={block.kind === ContentKind.IMAGE ? 'stacked' : 'inline'}
      onReplace={() => inputRef.current?.click()}
      onRemove={
        onRemove ??
        (() =>
          onChange({ assetId: null, assetUrl: null, assetMimeType: null, assetPosterUrl: null }))
      }
    />
  );

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={pick}
        disabled={readOnly || pending}
      />

      {block.assetId ? (
        block.kind === ContentKind.IMAGE ? (
          <div className="flex items-start gap-3">
            <div className="flex h-28 w-40 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line bg-page text-ink-ghost">
              {preview ? (
                <img src={preview} alt="Selected content" className="h-full w-full bg-white object-cover" />
              ) : (
                <FallbackIcon className="h-8 w-8" aria-hidden />
              )}
            </div>
            {actions}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="overflow-hidden rounded-lg border border-line bg-page">
              {preview ? (
                isVideo ? (
                  <video
                    src={preview}
                    controls
                    muted
                    playsInline
                    className="max-h-64 w-full bg-black"
                  />
                ) : (
                  <audio src={preview} controls className="w-full" />
                )
              ) : (
                <div className="flex h-16 items-center justify-center gap-2 text-sm text-ink-faint">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Loading {noun}…
                </div>
              )}
            </div>
            {actions}
          </div>
        )
      ) : readOnly ? (
        <p className="text-sm text-ink-faint">No {noun} set.</p>
      ) : (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={pending}
            className="focus-ring flex h-28 w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-line-dashed bg-page/50 text-sm text-ink-muted hover:border-ink-ghost hover:text-link disabled:cursor-not-allowed"
          >
            {pending ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : (
              <Upload className="h-5 w-5" aria-hidden />
            )}
            {pending ? 'Uploading…' : uploadLabel}
          </button>
          <p className="text-[0.6875rem] text-ink-faint">Up to 100MB.</p>
        </>
      )}
    </div>
  );
}

/**
 * Why this file can't be a LinkedIn carousel, or null when it can. Checked before uploading: a
 * 100 MB file that is refused after it has been sent is the same "no" delivered as slowly as
 * possible. The server checks again, and also counts the pages (LinkedIn takes up to 300).
 */
export function pdfFileProblem(file: { name: string; type: string; size: number }): string | null {
  if (!isDocumentMime(file.type)) {
    return `${file.name} isn't a PDF. A LinkedIn carousel is made from a PDF: save or export your slides as one.`;
  }
  if (file.size > LINKEDIN_DOCUMENT_MAX_BYTES) {
    return `${file.name} is ${(file.size / 1_000_000).toFixed(1)} MB. LinkedIn takes PDFs up to 100 MB.`;
  }
  return null;
}

/** What a PDF slot looks like empty: no file, no title. */
const NO_DOCUMENT: Partial<EditorBlock> = {
  assetId: null,
  assetUrl: null,
  assetMimeType: null,
  assetPosterUrl: null,
  assetThumbUrl: null,
  assetName: null,
  assetPageCount: null,
  text: '',
};

/**
 * The PDF panel: a multi-page PDF that LinkedIn shows as a swipeable carousel of its pages.
 *
 * Shows the first page - the only picture of the document there is, drawn at upload; never the PDF
 * itself, which an <img> cannot draw - with the file's name, its page count, and the title LinkedIn
 * requires above the pages. The title starts as the file's name and can be changed; left empty, the
 * file's name is what goes out, which is what the placeholder shows.
 */
export function DocumentBlockBody({
  block,
  readOnly,
  onChange,
  onRequestLibrary,
  uploading = false,
  onUploadCount,
}: {
  block: EditorBlock;
  readOnly: boolean;
  onChange: (patch: Partial<EditorBlock>) => void;
  onRequestLibrary?: () => void;
  /** See {@link MediaBox}'s `uploading` and `onUploadCount`. */
  uploading?: boolean;
  onUploadCount?: (anchorKey: string, delta: 1 | -1) => void;
}) {
  const { alert } = useDialogs();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const firstPage = useProxyImage(block.assetThumbUrl ?? null);

  const upload = useMutation({
    // While a PDF is on its way the slot is still empty, so the composer holds Save and Publish until
    // it lands. Counted under this block from the upload's own start and end - not from this panel
    // being drawn: a panel removed mid-upload holds them until the server has answered (the file
    // then lands nowhere), and so does a switch to another tab, while the file is still coming.
    onMutate: () => {
      onUploadCount?.(block.key, 1);
      return block.key;
    },
    mutationFn: (file: File) => api.uploadAsset(file),
    onSuccess: (asset) =>
      onChange(
        documentFields(block, {
          id: asset.id,
          blobUrl: asset.blobUrl,
          mimeType: asset.mimeType,
          posterUrl: null,
          thumbUrl: asset.thumbUrl,
          name: asset.name,
          pageCount: asset.pageCount,
        }),
      ),
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: err instanceof ApiRequestError ? err.message : 'Could not upload the PDF.',
      }),
    onSettled: (_asset, _error, _file, key) => {
      if (key) onUploadCount?.(key, -1);
    },
  });
  const pending = upload.isPending || uploading;

  const take = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    const problem = pdfFileProblem(file);
    if (problem) {
      void alert({ title: 'That file cannot be a carousel', message: problem });
      return;
    }
    upload.mutate(file);
  };

  const pages = block.assetPageCount;
  const pagesLabel = pages ? `${pages} page${pages === 1 ? '' : 's'}` : 'PDF';
  const fallbackTitle = documentTitleFromFileName(block.assetName);

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={DOCUMENT_MIME}
        className="hidden"
        onChange={(e) => {
          take(e.target.files);
          e.target.value = '';
        }}
        disabled={readOnly || pending}
      />

      {block.assetId ? (
        <div className="flex items-start gap-3">
          <div className="flex h-36 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line bg-page text-ink-ghost">
            {firstPage ? (
              <img src={firstPage} alt="First page" className="h-full w-full bg-white object-cover object-top" />
            ) : (
              <FileText className="h-8 w-8" aria-hidden />
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">{block.assetName ?? 'PDF'}</p>
              <p className="text-xs text-ink-muted">{pagesLabel} · Shown on LinkedIn as a swipeable carousel</p>
            </div>
            {readOnly ? (
              <p className="break-words text-sm text-ink-strong">
                <span className="text-ink-muted">Title: </span>
                {block.text.trim() || fallbackTitle || 'Untitled'}
              </p>
            ) : (
              <label className="block space-y-1">
                <span className="text-xs font-medium text-ink-label">Title, shown above the pages</span>
                <Input
                  value={block.text}
                  maxLength={DOCUMENT_TITLE_MAX_LENGTH}
                  placeholder={fallbackTitle || 'Shown above the pages on LinkedIn'}
                  onChange={(e) => onChange({ text: e.target.value })}
                />
              </label>
            )}
            {!readOnly && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={pending}
                  onClick={() => inputRef.current?.click()}
                >
                  Replace
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onChange(NO_DOCUMENT)}>
                  Remove PDF
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : readOnly ? (
        <p className="text-sm text-ink-faint">No PDF set.</p>
      ) : (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={pending}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              take(e.dataTransfer.files);
            }}
            className={cn(
              'focus-ring flex h-28 w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-3 text-center text-sm disabled:cursor-not-allowed',
              dragOver
                ? 'border-action bg-tint text-tint-fg'
                : 'border-line-dashed bg-page/50 text-ink-muted hover:border-ink-ghost hover:text-link',
            )}
          >
            {pending ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : (
              <Upload className="h-5 w-5" aria-hidden />
            )}
            {pending ? 'Uploading…' : 'Drag and drop a PDF, or click to upload'}
          </button>
          <div className="flex flex-wrap items-center gap-3">
            {onRequestLibrary && (
              <Button size="sm" disabled={pending} onClick={onRequestLibrary}>
                <FileText className="h-4 w-4" aria-hidden />
                Add from Media Library
              </Button>
            )}
            {/* The same pair, in the same order, as under a gallery: the library, then upload. */}
            <Button
              variant="secondary"
              size="sm"
              loading={pending}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-4 w-4" aria-hidden />
              Upload
            </Button>
            <span className="text-xs text-ink-faint">
              PDF up to 100 MB and {LINKEDIN_DOCUMENT_MAX_PAGES} pages. Each page becomes a slide.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/** Replace / Remove controls for a set media asset. */
function MediaActions({
  noun,
  pending,
  layout,
  onReplace,
  onRemove,
}: {
  noun: string;
  pending: boolean;
  layout: 'stacked' | 'inline';
  onReplace: () => void;
  onRemove: () => void;
}) {
  return (
    <div className={layout === 'stacked' ? 'flex flex-col gap-1.5' : 'flex gap-2'}>
      <Button variant="secondary" size="sm" loading={pending} onClick={onReplace}>
        Replace
      </Button>
      <Button variant="ghost" size="sm" onClick={onRemove}>
        Remove {noun}
      </Button>
    </div>
  );
}
